// src/components/scambi/ScambiCommon.js
// Componenti/utility condivisi tra le due sezioni della pagina Scambi
// (Win-To-Win e Scambi Liberi) — spostati qui da AuctionTradesTab.js, che
// prima li teneva privati dentro un solo tab di un'asta specifica.
import React, { useEffect, useMemo, useState } from 'react';
import '../../pages/AuctionAnalysisPage.css';
import TeamBadge from '../TeamBadge';
import AppTooltip from '../Tooltip';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { getPlayerById, getAllPlayerRatingsMap } from '../../firebase/playerService';
import { createPublicTrade } from '../../firebase/publicTradesService';
import { buildPlayerAvailability } from '../../utils/buildPlayerAvailability';
import { getPrimaryRoleValue, splitRoleTokens } from '../auction/auctionRosterUtils';
import { MANTRA_ROLE_THEME } from '../auction/MantraRoleConfiguration';
import {
  analyzeTradeProposal,
  assessTwoSidedTrade,
  buildComparisonRows,
  buildGroupTrendSeries,
  buildTradeBacktest,
  buildXiBacktest,
  buildTradeInsights,
} from '../auction/auctionAnalysis';
import { RoleChip, fmt, ROLE_LABELS } from '../auction/auctionCommon';
import {
  getUpcomingFixturesWithDifficulty,
  getUpcomingCalendarDifficulty,
  formatDifficultyValue,
} from '../../data/serieACalendar';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import {
  ArrowRightLeft,
  ExternalLink,
  Sparkles,
  Scale,
  Loader2,
  Lock,
  Globe2,
  X,
  Maximize2,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  CalendarDays,
  History,
  Bandage,
  Armchair,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';

export const ROLE_BUCKET_ORDER = ['P', 'D', 'C', 'A'];

export function openPlayerDetailNewTab(playerId) {
  window.open(`${window.location.origin}/players/${playerId}`, '_blank', 'noopener,noreferrer');
}

export function getPurchaseName(purchase) {
  return purchase?.playerSnapshot?.name || purchase?.playerName || '—';
}

export function getPurchaseTeam(purchase) {
  return purchase?.playerSnapshot?.team || purchase?.playerTeam || '—';
}

export function getPurchaseRole(purchase) {
  return purchase?.playerSnapshot?.role || purchase?.role || 'A';
}

// Avvolge un giocatore "nudo" (dal database globale, non da una rosa d'asta)
// in un oggetto a forma di "purchase", per riusare senza modifiche tutto il
// motore di analisi scambi (che si aspetta sempre purchase.playerSnapshot).
// Usato da Scambi Liberi, dove non esiste un vero acquisto/asta.
export function wrapPlayerAsPurchase(player) {
  if (!player) return null;
  return {
    playerId: String(player.id),
    playerName: player.name,
    playerTeam: player.team,
    role: player.role,
    credits: 0,
    playerSnapshot: player,
  };
}

// Snapshot leggero di un giocatore coinvolto in uno scambio, per salvarlo su
// Firestore (publicTrades) senza portarsi dietro l'intero oggetto purchase.
// Funziona sia con un purchase-shaped object (roster reali di Win-To-Win, o
// giocatori "liberi" già avvolti con wrapPlayerAsPurchase, entrambi con
// .playerSnapshot) sia con un giocatore "nudo" (fallback: entity stessa).
export function toTradeSnapshot(entity) {
  const snap = entity?.playerSnapshot || entity || {};
  const id = entity?.playerId ?? entity?.id;
  return {
    id: String(id),
    name: snap.name,
    team: snap.team,
    role: snap.role,
    value: snap.value,
    fmarkavg: snap.fmarkavg,
    markavg: snap.markavg,
    goals: snap.goals,
    assists: snap.assists,
    caps: snap.caps,
  };
}

export function groupPurchasesByBucket(purchases, rolesMode) {
  const groups = { P: [], D: [], C: [], A: [] };
  purchases.forEach((p) => {
    const bucket = getPrimaryRoleValue(getPurchaseRole(p), rolesMode);
    (groups[bucket] || (groups[bucket] = [])).push(p);
  });
  return groups;
}

export function buildRosterCounts(purchases = [], rolesMode) {
  const grouped = groupPurchasesByBucket(purchases, rolesMode);
  return { P: grouped.P.length, D: grouped.D.length, C: grouped.C.length, A: grouped.A.length };
}

export function DeltaTag({ value, suffix = '' }) {
  if (value > 0.01) {
    return <span className="delta-tag delta-tag--up"><TrendingUp size={12} /> +{fmt(value)}{suffix}</span>;
  }
  if (value < -0.01) {
    return <span className="delta-tag delta-tag--down"><TrendingDown size={12} /> {fmt(value)}{suffix}</span>;
  }
  return <span className="delta-tag delta-tag--flat"><Minus size={12} /> 0{suffix}</span>;
}

// Chip ruolo per gli scambi: in Mantra mostra i ruoli mantra reali del giocatore
// (Dc, W/T, Pc...) con i colori di MANTRA_ROLE_THEME, come in AuctionMantraManageTab;
// in Classic il consueto chip P/D/C/A.
export function PlayerRoleBadge({ role, rolesMode }) {
  if (rolesMode !== 'mantra') {
    return <RoleChip role={getPrimaryRoleValue(role, rolesMode)} />;
  }

  const tokens = splitRoleTokens(role);
  if (!tokens.length) {
    return <RoleChip role={getPrimaryRoleValue(role, rolesMode)} />;
  }

  return (
    <span className="mantra-role-chips" data-count={tokens.length}>
      {tokens.map((token) => {
        const theme = MANTRA_ROLE_THEME[token] || MANTRA_ROLE_THEME.Dc;
        return (
          <span
            key={token}
            className="mantra-role-chip"
            style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.color }}
          >
            {token}
          </span>
        );
      })}
    </span>
  );
}

export function PlayerPickRow({ purchase, checked, onToggle, rolesMode }) {
  return (
    <label className={`player-pick-row${checked ? ' player-pick-row--checked' : ''}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} />
      <TeamBadge team={getPurchaseTeam(purchase)} size={28} />
      <div className="player-pick-row__body">
        <strong>{getPurchaseName(purchase)}</strong>
        <span>{purchase.credits} cr</span>
      </div>
      <PlayerRoleBadge role={getPurchaseRole(purchase)} rolesMode={rolesMode} />
    </label>
  );
}

export function GroupedPlayerPickList({ grouped, rolesMode, selectedIds, onToggle, emptyLabel }) {
  const hasAny = ROLE_BUCKET_ORDER.some((bucket) => grouped[bucket]?.length);

  if (!hasAny) {
    return <div className="trade-picker__empty">{emptyLabel}</div>;
  }

  return (
    <>
      {ROLE_BUCKET_ORDER.map((bucket) => (
        grouped[bucket]?.length ? (
          <div key={bucket} className="trade-picker__roleGroup">
            <div className="trade-picker__roleGroupHead">
              <RoleChip role={bucket} />
              <span>{ROLE_LABELS[bucket]}</span>
            </div>
            {grouped[bucket].map((purchase) => (
              <PlayerPickRow
                key={purchase.playerId}
                purchase={purchase}
                rolesMode={rolesMode}
                checked={selectedIds.has(String(purchase.playerId))}
                onToggle={() => onToggle(purchase.playerId)}
              />
            ))}
          </div>
        ) : null
      ))}
    </>
  );
}

export function VerdictBanner({ result }) {
  if (!result) return null;

  const config = {
    me: { label: 'Scambio vantaggioso per te!', tone: 'good', Icon: TrendingUp },
    loro: { label: `Scambio sfavorevole per te!`, tone: 'bad', Icon: TrendingDown },
    equilibrato: { label: 'Scambio equilibrato', tone: 'neutral', Icon: Scale },
  }[result.verdict];

  const Icon = config.Icon;

  return (
    <div className={`verdict-banner verdict-banner--${config.tone}`}>
      <Icon size={18} />
      <strong>{config.label}</strong>
    </div>
  );
}

const STANCE_META = {
  gain: { label: 'Ci guadagna', tone: 'good', Icon: TrendingUp },
  fair: { label: 'Scambio equo', tone: 'neutral', Icon: Minus },
  loss: { label: 'Ci rimette', tone: 'bad', Icon: TrendingDown },
};

function VerdictSideCard({ who, side, accent }) {
  const meta = STANCE_META[side?.stance] || STANCE_META.fair;
  const Icon = meta.Icon;
  return (
    <div className={`two-verdict__card two-verdict__card--${accent}`}>
      <div className="two-verdict__cardHead">
        <span className="two-verdict__who">{who}</span>
        <span className={`two-verdict__stance two-verdict__stance--${meta.tone}`}>
          <Icon size={13} /> {meta.label}
        </span>
      </div>
      <ul className="two-verdict__reasons">
        {(side?.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
      </ul>
    </div>
  );
}

// Verdetto a due facce: mostra separatamente se conviene a ME e alla
// CONTROPARTE, con le motivazioni per ciascun lato. Sostituisce il singolo
// banner "vantaggioso/sfavorevole", che nascondeva il punto di vista dell'altro.
export function TwoSidedVerdict({ assessment }) {
  if (!assessment) return null;
  return (
    <div className="two-verdict">
      <div className="two-verdict__headline">
        <Scale size={15} />
        <strong>{assessment.headline}</strong>
      </div>
      <div className="two-verdict__cards">
        <VerdictSideCard who={assessment.myTeamName || 'Per te'} side={assessment.forMe} accent="mine" />
        <VerdictSideCard who={assessment.theirTeamName || 'Per la controparte'} side={assessment.forThem} accent="theirs" />
      </div>
    </div>
  );
}

export function RoleTargetOverlay({ buckets, onSelect, onClose }) {
  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-panel role-target-overlay glass" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-head">
          <div className="overlay-head__title">
            <div>
              <span>Scambio multi-ruolo</span>
              <h3>In quale ruolo vuoi rinforzarti?</h3>
            </div>
          </div>
          <button className="overlay-close" type="button" onClick={onClose} aria-label="Chiudi">
            <X size={18} />
          </button>
        </div>

        <p className="role-target-overlay__hint">
          I pacchetti proposti manterranno sempre gli stessi ruoli che stai offrendo (i limiti di rosa non si possono superare):
          questa scelta serve solo a dare priorità al rinforzo nel reparto che preferisci.
        </p>

        <div className="role-target-overlay__options">
          {buckets.map((bucket) => (
            <button key={bucket} type="button" className="role-target-overlay__option" onClick={() => onSelect(bucket)}>
              <RoleChip role={bucket} />
              {ROLE_LABELS[bucket] || bucket}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TradeChartTooltip({ active, payload, label, isDark }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip" style={{ background: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#f8fafc' : '#111827' }}>
      <strong className="chart-tooltip__label">Giornata {label}</strong>
      {payload.map((item) => (
        <div key={item.dataKey} className="chart-tooltip__row">
          <span className="chart-tooltip__dot" style={{ background: item.color }} />
          {item.name}: <strong>{typeof item.value === 'number' ? item.value.toFixed(2) : '—'}</strong>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ trend, myTeamName, theirTeamName, isDark }) {
  const chartText = isDark ? '#e5e7eb' : '#111827';
  const gridColor = isDark ? 'rgba(255,255,255,.10)' : 'rgba(17,24,39,.10)';

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={trend} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
        <CartesianGrid stroke={gridColor} strokeDasharray="4 4" />
        <XAxis dataKey="day" tick={{ fill: chartText, fontSize: 11 }} />
        <YAxis tick={{ fill: chartText, fontSize: 11 }} domain={[0, 10]} />
        <Tooltip content={<TradeChartTooltip isDark={isDark} />} />
        <Legend wrapperStyle={{ color: chartText, fontSize: 12 }} />
        <Line type="monotone" dataKey="mine" name={myTeamName || 'Tuoi giocatori'} stroke="#7c5cff" strokeWidth={2.5} dot={false} connectNulls activeDot={{ r: 5 }} />
        <Line type="monotone" dataKey="theirs" name={theirTeamName || 'In arrivo'} stroke="#31d0aa" strokeWidth={2.5} dot={false} connectNulls activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// "Prova del nove": non stima, guarda cos'è già successo in campo.
function backtestStance(delta) {
  if (delta > 1.5) return { tone: 'good', Icon: TrendingUp, label: 'a tuo favore' };
  if (delta < -1.5) return { tone: 'bad', Icon: TrendingDown, label: 'a sfavore' };
  return { tone: 'neutral', Icon: Minus, label: 'in pareggio' };
}

export function TradeBacktestCard({ backtest, xiBacktest, myTeamName, theirTeamName }) {
  if (!backtest && !xiBacktest) return null;
  const pkg = backtest;
  const pkgStance = pkg ? backtestStance(pkg.delta) : null;
  const PkgIcon = pkgStance?.Icon;
  const xiStance = xiBacktest ? backtestStance(xiBacktest.myDelta) : null;
  const XiIcon = xiStance?.Icon;

  return (
    <div className="detail-card glass backtest-card">
      <div className="detail-card__head">
        <div>
          <h2>Prova del nove</h2>
          <span>Cos'è già successo in campo — fantavoti reali, non una previsione</span>
        </div>
        <div className="detail-card__pill"><History size={14} /><span>Storico</span></div>
      </div>

      {pkg && (
        <div className="backtest-row">
          <div className="backtest-row__label">
            Ultime {pkg.days} giornate giocate (dalla {pkg.fromDay}<sup>a</sup> alla {pkg.toDay}<sup>a</sup>) —
            fantapunti reali totalizzati dai giocatori dello scambio
          </div>
          <div className="backtest-bars">
            <div className="backtest-bar">
              <span className="backtest-bar__name">Chi ricevi</span>
              <span className="backtest-bar__val">{pkg.receive.total}</span>
              <span className="backtest-bar__games">{pkg.receive.games} pres.</span>
            </div>
            <div className="backtest-bar">
              <span className="backtest-bar__name">Chi cedi</span>
              <span className="backtest-bar__val">{pkg.give.total}</span>
              <span className="backtest-bar__games">{pkg.give.games} pres.</span>
            </div>
          </div>
          <div className={`backtest-verdict backtest-verdict--${pkgStance.tone}`}>
            <PkgIcon size={14} />
            <strong>{pkg.delta > 0 ? '+' : ''}{pkg.delta} fantapunti</strong> {pkgStance.label}:
            {pkg.delta > 1.5 && ' i giocatori in arrivo hanno reso di più.'}
            {pkg.delta < -1.5 && ' i giocatori che cederesti hanno reso di più.'}
            {Math.abs(pkg.delta) <= 1.5 && ' i due pacchetti hanno reso quasi uguale.'}
          </div>
        </div>
      )}

      {xiBacktest && (
        <div className="backtest-row">
          <div className="backtest-row__label">
            Simulazione sull'11 titolare, stesse {xiBacktest.days} giornate: quanti fantapunti
            avrebbe fatto in più (o in meno) la formazione di ciascuno, con lo scambio
          </div>
          <div className="backtest-bars">
            <div className="backtest-bar">
              <span className="backtest-bar__name">{myTeamName || 'Tu'}</span>
              <span className={`backtest-bar__val ${xiBacktest.myDelta > 0 ? 'is-up' : xiBacktest.myDelta < 0 ? 'is-down' : ''}`}>
                {xiBacktest.myDelta > 0 ? '+' : ''}{xiBacktest.myDelta}
              </span>
            </div>
            <div className="backtest-bar">
              <span className="backtest-bar__name">{theirTeamName || 'Controparte'}</span>
              <span className={`backtest-bar__val ${xiBacktest.theirDelta > 0 ? 'is-up' : xiBacktest.theirDelta < 0 ? 'is-down' : ''}`}>
                {xiBacktest.theirDelta > 0 ? '+' : ''}{xiBacktest.theirDelta}
              </span>
            </div>
          </div>
          <div className={`backtest-verdict backtest-verdict--${xiStance.tone}`}>
            <XiIcon size={14} />
            {xiBacktest.myDelta > 1.5 && `Nelle giornate passate lo scambio avrebbe rafforzato la tua formazione (${xiBacktest.myDelta > 0 ? '+' : ''}${xiBacktest.myDelta} fantapunti sull'11).`}
            {xiBacktest.myDelta < -1.5 && `Nelle giornate passate lo scambio avrebbe indebolito la tua formazione (${xiBacktest.myDelta} fantapunti sull'11).`}
            {Math.abs(xiBacktest.myDelta) <= 1.5 && 'Sull\'11 titolare, nelle giornate passate, sarebbe cambiato pochissimo.'}
          </div>
        </div>
      )}

      <p className="backtest-note">
        È il rendimento reale già andato in archivio, non tiene conto di formazione/turnover futuri
        né di infortuni: serve come controprova concreta del verdetto qui sopra.
      </p>
    </div>
  );
}

export function TradeComparisonTable({ title, rows, accent, showCredits = true }) {
  const count = rows.length || 1;
  const totals = rows.reduce(
    (acc, r) => ({
      price: acc.price + r.price,
      caps: acc.caps + r.caps,
      markavg: acc.markavg + r.markavg,
      fmarkavg: acc.fmarkavg + r.fmarkavg,
      goals: acc.goals + r.goals,
      assists: acc.assists + r.assists,
    }),
    { price: 0, caps: 0, markavg: 0, fmarkavg: 0, goals: 0, assists: 0 }
  );

  return (
    <div className={`trade-compare-table trade-compare-table--${accent}`}>
      <div className="trade-compare-table__head">
        <strong>{title}</strong>
        <span>{rows.length} giocatori</span>
      </div>
      <div className="trade-compare-table__scroll">
        <table>
          <thead>
            <tr>
              <th>Giocatore</th>
              <th>Ruolo</th>
              {showCredits && <th>Prezzo</th>}
              <th>PG</th>
              <th>MV</th>
              <th>FV</th>
              <th>Gol</th>
              <th>Ass</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <button type="button" className="trade-compare-table__playerBtn" onClick={() => openPlayerDetailNewTab(row.id)}>
                    {row.name}
                    <ExternalLink size={11} />
                  </button>
                  <span className="trade-compare-table__team">{row.team}</span>
                </td>
                <td><span className="role-text-pill">{row.role}</span></td>
                {showCredits && <td>{row.price} cr</td>}
                <td>{row.caps}</td>
                <td>{fmt(row.markavg)}</td>
                <td>{fmt(row.fmarkavg)}</td>
                <td>{row.goals}</td>
                <td>{row.assists}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td>Totale / Media</td>
                <td>—</td>
                {showCredits && <td>{totals.price} cr</td>}
                <td>{totals.caps}</td>
                <td>{fmt(totals.markavg / count)}</td>
                <td>{fmt(totals.fmarkavg / count)}</td>
                <td>{totals.goals}</td>
                <td>{totals.assists}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── Prossime partite dei club dei giocatori coinvolti nello scambio ─────────

const FIXTURE_DIFFICULTY_LABELS = {
  1: 'molto abbordabile',
  2: 'abbordabile',
  3: 'media',
  4: 'difficile',
  5: 'molto difficile',
};

function FixtureChip({ fixture }) {
  // La difficoltà è già corretta per il fattore campo (mezzi punti): per
  // colore ed etichetta si usa il livello arrotondato 1-5.
  const level = Math.min(5, Math.max(1, Math.round(fixture.difficulty)));
  const tooltip = `Giornata ${fixture.giornata} · ${fixture.home ? 'in casa' : 'in trasferta'} · difficoltà ${formatDifficultyValue(fixture.difficulty)}/5 (${FIXTURE_DIFFICULTY_LABELS[level]})`;
  return (
    <AppTooltip as="span" className={`fixture-chip fixture-chip--d${level}`} content={tooltip}>
      <span className="fixture-chip__opp">
        <span className="fixture-chip__venue">{fixture.home ? 'C' : 'T'}</span>
        {fixture.opponent}
      </span>
    </AppTooltip>
  );
}

function PlayerFixturesRow({ purchase }) {
  const team = getPurchaseTeam(purchase);
  const fixtures = getUpcomingFixturesWithDifficulty(team, 7);
  const avgDifficulty = getUpcomingCalendarDifficulty(team, 7);

  return (
    <div className="fixtures-player">
      <div className="fixtures-player__head">
        <TeamBadge team={team} size={22} />
        <strong>{getPurchaseName(purchase)}</strong>
        <span>{team}</span>
        {avgDifficulty !== null && (
          <AppTooltip
            as="span"
            className="fixtures-player__avg"
            content="Difficoltà media delle prossime 7 giornate (1 = facile, 5 = durissima); le trasferte pesano leggermente di più"
          >
            media {fmt(avgDifficulty)}
          </AppTooltip>
        )}
      </div>
      {fixtures.length ? (
        <div className="fixtures-player__list">
          {fixtures.map((fixture) => (
            <FixtureChip key={fixture.giornata} fixture={fixture} />
          ))}
        </div>
      ) : (
        <span className="fixtures-player__empty">Calendario Serie A non disponibile per questo club.</span>
      )}
    </div>
  );
}

function TradeFixturesCard({ mySelected, theirSelected, myTeamName, theirTeamName }) {
  return (
    <div className="detail-card glass">
      <div className="detail-card__head">
        <div>
          <h2>Prossime 7 partite</h2>
          <span>Impegni Serie A dei club dei giocatori coinvolti · verde = abbordabile, rosso = big match · C = in casa, T = in trasferta</span>
        </div>
        <div className="detail-card__pill"><CalendarDays size={14} /><span>Calendario</span></div>
      </div>
      <div className="trade-fixtures__columns">
        <div className="trade-fixtures__side trade-fixtures__side--mine">
          <span className="trade-fixtures__sideLabel">{myTeamName || 'Offri'}</span>
          {mySelected.map((p) => <PlayerFixturesRow key={p.playerId} purchase={p} />)}
        </div>
        <div className="trade-fixtures__side trade-fixtures__side--theirs">
          <span className="trade-fixtures__sideLabel">{theirTeamName || 'Ricevi'}</span>
          {theirSelected.map((p) => <PlayerFixturesRow key={p.playerId} purchase={p} />)}
        </div>
      </div>
    </div>
  );
}

export function TradeAnalysisReport({
  mySelected,
  theirSelected,
  myTeamName,
  theirTeamName,
  rolesMode,
  auction,
  myRosterCounts,
  theirRosterCounts,
  myRoster = null,
  theirRoster = null,
  showCredits = true,
}) {
  const { isDark } = useTheme();

  const [availabilityById, setAvailabilityById] = useState(null);
  // Voti storici dei soli giocatori scambiati (stessa fetch usata più sotto
  // per trend/backtest, nessuna chiamata in più): alimenta il segnale "in un
  // momento"/"in calo" di assessTwoSidedTrade (vedi computeRecentFormDelta in
  // auctionAnalysis.js).
  const [tradedRatingsById, setTradedRatingsById] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setAvailabilityById(null);
    const roster = [
      ...mySelected.map((p) => ({ id: String(p.playerId), name: getPurchaseName(p) })),
      ...theirSelected.map((p) => ({ id: String(p.playerId), name: getPurchaseName(p) })),
    ];
    buildPlayerAvailability(roster)
      .then((map) => { if (!cancelled) setAvailabilityById(map); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mySelected, theirSelected]);

  const analysis = useMemo(
    () => analyzeTradeProposal({ mySelected, theirSelected, myTeamName, theirTeamName, rolesMode, auction, myRosterCounts, theirRosterCounts }),
    [mySelected, theirSelected, myTeamName, theirTeamName, rolesMode, auction, myRosterCounts, theirRosterCounts]
  );

  const assessment = useMemo(
    () => assessTwoSidedTrade({ mySelected, theirSelected, myTeamName, theirTeamName, rolesMode, auction, myRoster, theirRoster, availabilityById, ratingsById: tradedRatingsById }),
    [mySelected, theirSelected, myTeamName, theirTeamName, rolesMode, auction, myRoster, theirRoster, availabilityById, tradedRatingsById]
  );

  const myRows = useMemo(() => buildComparisonRows(mySelected), [mySelected]);
  const theirRows = useMemo(() => buildComparisonRows(theirSelected), [theirSelected]);
  const insights = useMemo(() => buildTradeInsights({ analysis, myRows, theirRows }), [analysis, myRows, theirRows]);

  const availabilityNotes = useMemo(() => {
    if (!availabilityById) return [];
    const notes = [];
    const scan = (list, dir) => list.forEach((p) => {
      const a = availabilityById.get(String(p.playerId));
      if (!a) return;
      const name = getPurchaseName(p);
      const where = dir === 'in' ? 'in arrivo' : 'che cedi';
      if (a.injury) {
        const back = a.injury.expectedReturn ? `, rientro previsto ${a.injury.expectedReturn}` : '';
        notes.push({ kind: 'injury', text: `${name} (${where}) è infortunato — ${a.injury.reason}${back}.` });
      } else if (a.lineup?.status === 'bench') {
        notes.push({ kind: 'bench', text: `${name} (${where}) è dato in panchina nelle probabili formazioni.` });
      }
    });
    scan(theirSelected, 'in');
    scan(mySelected, 'out');
    return notes;
  }, [availabilityById, mySelected, theirSelected]);

  const [trend, setTrend] = useState(null);
  const [trendFailed, setTrendFailed] = useState(false);
  // "Prova del nove": fantavoti reali già giocati nelle ultime giornate.
  const [backtest, setBacktest] = useState(null);   // pacchetti (sempre)
  const [xiBacktest, setXiBacktest] = useState(null); // 11 titolare (solo con rose)

  const hasRosters = Array.isArray(myRoster) && Array.isArray(theirRoster);

  useEffect(() => {
    let cancelled = false;
    setTrend(null);
    setTrendFailed(false);
    setBacktest(null);
    setXiBacktest(null);
    setTradedRatingsById(null);

    const ids = [...new Set([...mySelected, ...theirSelected].map((p) => String(p.playerId)))];

    Promise.all(ids.map((id) => getPlayerById(id).catch(() => null)))
      .then((results) => {
        if (cancelled) return;
        const byId = new Map();
        results.forEach((p) => { if (p) byId.set(String(p.id), p); });
        setTradedRatingsById(byId);

        const mySidePlayers = mySelected.map((p) => byId.get(String(p.playerId))).filter(Boolean);
        const theirSidePlayers = theirSelected.map((p) => byId.get(String(p.playerId))).filter(Boolean);

        if (!mySidePlayers.length && !theirSidePlayers.length) {
          setTrendFailed(true);
          return;
        }

        setTrend(buildGroupTrendSeries({ mySidePlayers, theirSidePlayers }));
        setBacktest(buildTradeBacktest({ givePlayers: mySidePlayers, receivePlayers: theirSidePlayers }));
      })
      .catch(() => {
        if (!cancelled) setTrendFailed(true);
      });

    return () => { cancelled = true; };
  }, [mySelected, theirSelected]);

  useEffect(() => {
    if (!hasRosters) return undefined;
    let cancelled = false;
    setXiBacktest(null);
    getAllPlayerRatingsMap()
      .then((ratingsById) => {
        if (cancelled) return;
        setXiBacktest(buildXiBacktest({
          myRoster, theirRoster, mySelected, theirSelected,
          ratingsById, auction, rolesMode,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mySelected, theirSelected, myRoster, theirRoster, auction, rolesMode, hasRosters]);

  return (
    <div className="trade-report">
      <TwoSidedVerdict assessment={assessment} />

      {availabilityNotes.length > 0 && (
        <div className="avail-strip">
          {availabilityNotes.map((n, i) => (
            <div key={i} className={`avail-strip__row avail-strip__row--${n.kind}`}>
              {n.kind === 'injury' ? <Bandage size={14} /> : <Armchair size={14} />}
              <span>{n.text}</span>
            </div>
          ))}
        </div>
      )}

      <div className="proposal-result__grid">
        <div><span>Qualità che offri</span><strong>{fmt(analysis.myScoreOut)}</strong></div>
        <div><span>Qualità che ricevi</span><strong>{fmt(analysis.theirScoreOut)}</strong></div>
        <div><span>Delta qualità per te</span><DeltaTag value={analysis.netScoreForMe} /></div>
        {showCredits && (
          <>
            <div><span>Crediti dati</span><strong>{analysis.myPriceOut} cr</strong></div>
            <div><span>Crediti ricevuti</span><strong>{analysis.theirPriceOut} cr</strong></div>
            <div><span>Delta crediti per te</span><DeltaTag value={analysis.netCreditsForMe} suffix=" cr" /></div>
          </>
        )}
      </div>

      {analysis.warnings.length > 0 && (
        <div className="proposal-result__warnings">
          {analysis.warnings.map((warn, idx) => (
            <div key={idx} className="proposal-warning">
              <AlertTriangle size={13} />
              {warn}
            </div>
          ))}
        </div>
      )}

      {insights.length > 0 && (
        <div className="trade-report__insights">
          <div className="trade-report__insightsHead">
            <Sparkles size={14} />
            <strong>Cosa dicono i numeri</strong>
          </div>
          <ul>
            {insights.map((text, idx) => <li key={idx}>{text}</li>)}
          </ul>
        </div>
      )}

      <div className="trade-compare-columns trade-compare-columns--report">
        <TradeComparisonTable title={myTeamName || 'La tua squadra'} rows={myRows} accent="mine" showCredits={showCredits} />
        <TradeComparisonTable title={theirTeamName || 'Squadra avversaria'} rows={theirRows} accent="theirs" showCredits={showCredits} />
      </div>

      <TradeFixturesCard
        mySelected={mySelected}
        theirSelected={theirSelected}
        myTeamName={myTeamName}
        theirTeamName={theirTeamName}
      />

      <TradeBacktestCard
        backtest={backtest}
        xiBacktest={xiBacktest}
        myTeamName={myTeamName}
        theirTeamName={theirTeamName}
      />

      <div className="detail-card glass">
        <div className="detail-card__head">
          <div>
            <h2>Andamento fantavoto stagionale</h2>
            <span>Media giornata per giornata tra i giocatori coinvolti</span>
          </div>
          <div className="detail-card__pill"><TrendingUp size={14} /><span>Trend</span></div>
        </div>
        {trend === null ? (
          <div className="trade-report__chartLoading"><Loader2 size={18} className="spin" /> Carico lo storico voti...</div>
        ) : trendFailed || !trend.length ? (
          <div className="detail-empty-inline">Storico voti non disponibile per i giocatori coinvolti.</div>
        ) : (
          <TrendChart trend={trend} myTeamName={myTeamName} theirTeamName={theirTeamName} isDark={isDark} />
        )}
      </div>
    </div>
  );
}

// ── Pacchetto di scambio "Offri ⇄ Ricevi" (condiviso tra suggerimenti e AI) ──

function TradePackagePlayer({ purchase, rolesMode }) {
  return (
    <div className="pkg-player">
      <div className="pkg-player__body">
        <strong>{getPurchaseName(purchase)}</strong>
        <span>{getPurchaseTeam(purchase)} · {purchase.credits} cr</span>
      </div>
      <PlayerRoleBadge role={getPurchaseRole(purchase)} rolesMode={rolesMode} />
    </div>
  );
}

export function TradePackageFlow({ give, receive, rolesMode, outLabel = 'Offri', inLabel = 'Ricevi' }) {
  return (
    <div className="pkg-flow">
      <div className="pkg-flow__side pkg-flow__side--out">
        <span className="pkg-flow__label">{outLabel}</span>
        {give.map((p) => (
          <TradePackagePlayer key={p.playerId} purchase={p} rolesMode={rolesMode} />
        ))}
      </div>
      <div className="pkg-flow__arrow" aria-hidden="true">
        <ArrowRightLeft size={15} />
      </div>
      <div className="pkg-flow__side pkg-flow__side--in">
        <span className="pkg-flow__label">{inLabel}</span>
        {receive.map((p) => (
          <TradePackagePlayer key={p.playerId} purchase={p} rolesMode={rolesMode} />
        ))}
      </div>
    </div>
  );
}

export function AcceptanceBadge({ acceptance }) {
  const info = acceptance >= 7
    ? { label: 'Sì probabile', tone: 'high' }
    : acceptance >= 5
      ? { label: 'Trattabile', tone: 'mid' }
      : { label: 'Difficile', tone: 'low' };

  return (
    <AppTooltip
      as="span"
      className={`accept-badge accept-badge--${info.tone}`}
      content="Stima di quanto è probabile che l'altra squadra accetti (10 = molto probabile)"
    >
      <Scale size={12} />
      {info.label} · {acceptance}/10
    </AppTooltip>
  );
}

export function RoleTradeoffRow({ bucketDeltas }) {
  if (!bucketDeltas || bucketDeltas.length < 2) return null;

  return (
    <div className="role-tradeoff-row">
      <span className="role-tradeoff-row__label">Bilancio per ruolo</span>
      <div className="role-tradeoff-row__items">
        {bucketDeltas.map(({ bucket, delta }) => (
          <span key={bucket} className="role-tradeoff-row__item">
            <RoleChip role={bucket} />
            <DeltaTag value={delta} />
          </span>
        ))}
      </div>
    </div>
  );
}

export function GroupSuggestionCard({ combo, offeredPlayers, rolesMode, onOpenAnalysis }) {
  return (
    <article className="group-suggestion-card glass">
      <div className="group-suggestion-card__head">
        <div className="group-suggestion-card__headLeft">
          <strong>
            {combo.players.length === 1 && offeredPlayers.length === 1
              ? 'Scambio 1 per 1'
              : `Pacchetto ${offeredPlayers.length} per ${combo.players.length}`}
          </strong>
          {typeof combo.acceptance === 'number' && <AcceptanceBadge acceptance={combo.acceptance} />}
        </div>
        <div className="group-suggestion-card__gain">
          <DeltaTag value={combo.scoreDelta} />
          <span className="group-suggestion-card__price">
            {combo.combinedPrice} cr {combo.priceDelta !== 0 ? `(${combo.priceDelta > 0 ? '+' : ''}${combo.priceDelta})` : ''}
          </span>
        </div>
      </div>

      <TradePackageFlow give={offeredPlayers} receive={combo.players} rolesMode={rolesMode} />

      {combo.availabilityFlags?.length > 0 && (
        <div className="avail-strip avail-strip--compact">
          {combo.availabilityFlags.map((f) => (
            <div key={f.playerId} className={`avail-strip__row avail-strip__row--${f.kind}`}>
              {f.kind === 'injury' ? <Bandage size={13} /> : <Armchair size={13} />}
              <span><strong>{f.name}</strong> in arrivo: {f.kind === 'injury' ? `infortunato (${f.detail})` : f.detail}</span>
            </div>
          ))}
        </div>
      )}

      <RoleTradeoffRow bucketDeltas={combo.bucketDeltas} />

      {combo.whyThem?.length > 0 && (
        <div className="group-suggestion-card__why">
          <span className="group-suggestion-card__whyTitle"><ThumbsUp size={12} /> Perché possono dirti di sì</span>
          <ul>
            {combo.whyThem.map((reason, idx) => <li key={idx}>{reason}</li>)}
          </ul>
        </div>
      )}

      {combo.whyNot?.length > 0 && (
        <div className="group-suggestion-card__why group-suggestion-card__why--no">
          <span className="group-suggestion-card__whyTitle"><ThumbsDown size={12} /> Perché potrebbero dirti di no</span>
          <ul>
            {combo.whyNot.map((reason, idx) => <li key={idx}>{reason}</li>)}
          </ul>
        </div>
      )}

      {combo.hardGapDelta < 0 && (
        <div className="group-suggestion-card__mineNote group-suggestion-card__mineNote--cover">
          <TrendingUp size={13} />
          Copri un reparto che era scoperto: smetti di rischiare di giocare in 10.
        </div>
      )}
      {combo.hardGapDelta >= 0 && combo.coverageDelta > 3 && (
        <div className="group-suggestion-card__mineNote group-suggestion-card__mineNote--cover">
          <TrendingUp size={13} />
          Migliora la copertura dei reparti: più alternative vere in panchina.
        </div>
      )}
      {combo.myStartersDelta > 0.01 && (
        <div className="group-suggestion-card__mineNote">
          <TrendingUp size={13} />
          Con questo scambio anche la tua formazione titolare migliora, non solo la rosa.
        </div>
      )}
      {combo.hardGapDelta > 0 && (
        <div className="group-suggestion-card__why group-suggestion-card__why--no">
          <span className="group-suggestion-card__whyTitle">Attenzione</span>
          <ul><li>Dopo questo scambio un reparto resterebbe sotto i titolari: rischi di giocare in 10.</li></ul>
        </div>
      )}

      <button type="button" className="group-suggestion-card__toggle" onClick={onOpenAnalysis}>
        <Maximize2 size={14} />
        Vedi analisi completa
      </button>
    </article>
  );
}

// ── Pubblica uno scambio analizzato sulla Community, votabile da tutti per 4
// giorni. Condiviso tra Scambi Liberi (kind="liberi") e Scambi Win-To-Win
// (kind="wintowin", con meta = { auctionName, myTeamName, theirTeamName } per
// dare contesto a chi vota). Il chiamante forza un remount (via `key`) quando
// cambia la proposta analizzata, così lo stato "pubblicato" si resetta da solo.
export function PublishTradeControl({
  mySelected,
  theirSelected,
  myTeamName,
  theirTeamName,
  rolesMode,
  auction,
  myRosterCounts,
  theirRosterCounts,
  kind = 'liberi',
  meta = null,
  onGoToCommunity,
}) {
  const { user } = useAuth();
  const [makePublic, setMakePublic] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishedId, setPublishedId] = useState(null);

  const handleConfirm = async () => {
    if (!user?.uid) return;
    setPublishing(true);
    try {
      const analysis = analyzeTradeProposal({
        mySelected,
        theirSelected,
        myTeamName,
        theirTeamName,
        rolesMode,
        auction,
        myRosterCounts,
        theirRosterCounts,
      });
      const myRows = buildComparisonRows(mySelected);
      const theirRows = buildComparisonRows(theirSelected);
      const insights = buildTradeInsights({ analysis, myRows, theirRows });

      const id = await createPublicTrade({
        ownerUid: user.uid,
        ownerDisplayName: user.displayName || user.email || 'Utente FantApp',
        offeredPlayers: mySelected.map(toTradeSnapshot),
        receivedPlayers: theirSelected.map(toTradeSnapshot),
        analysis: { verdict: analysis.verdict, netScoreForMe: analysis.netScoreForMe, insights },
        kind,
        meta,
      });

      setPublishedId(id);
    } finally {
      setPublishing(false);
    }
  };

  if (publishedId) {
    return (
      <div className="scambi-published-note">
        <Globe2 size={15} />
        Scambio pubblicato: gli altri utenti FantApp possono votarlo per i prossimi 4 giorni.
        {onGoToCommunity && (
          <button type="button" className="scambi-published-note__link" onClick={onGoToCommunity}>
            Vai alla Community
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="scambi-publish-row">
      <label className="scambi-publish-toggle">
        <input type="checkbox" checked={makePublic} onChange={(e) => setMakePublic(e.target.checked)} />
        {makePublic ? <Globe2 size={16} /> : <Lock size={16} />}
        Rendi pubblico questo scambio (votabile da tutti per 4 giorni)
      </label>

      <button type="button" className="confirm-btn" disabled={!makePublic || publishing} onClick={handleConfirm}>
        {publishing ? <Loader2 size={16} className="spin" /> : <Globe2 size={16} />}
        Conferma e pubblica
      </button>
    </div>
  );
}

export function TradeAnalysisOverlay({ onClose, children }) {
  return (
    <div className="overlay-backdrop" onClick={onClose}>
      <div className="overlay-panel overlay-panel--wide glass" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-head">
          <div className="overlay-head__title">
            <div>
              <span>Analisi scambio</span>
              <h3>Ci guadagni?</h3>
            </div>
          </div>
          <button className="overlay-close" type="button" onClick={onClose} aria-label="Chiudi">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
